import React from 'react';

import { useTranslation } from 'react-i18next';
import { set } from 'lodash';
import { Form } from 'semantic';

import { AccountPlatformFeatureProvider } from 'components/account-platform-features/context';
import {
  TariffProfile,
  TariffProfileCustomIssuerTariff,
} from 'types/tariffprofile';
import { TariffProfileFeatureProvider } from './TariffProfileFeatureProvider';
import { useUser } from 'contexts/user';
import { TariffCostSettings } from './TariffCostSettings';
import { ActionButtonWithIcon } from '../../semantic/ActionButtonWithIcon';

interface Props {
  formValues: TariffProfile;
  onChange: (next: TariffProfileCustomIssuerTariff[]) => void;
  sameForAcDc: boolean;
}

type Selected = {
  ac: string;
  dc: string;
};

export default function TariffProfileCustomIssuerTariffInput({
  formValues,
  onChange,
  sameForAcDc,
}: Props) {
  const { user } = useUser();
  const { t } = useTranslation();

  const addTariff = () => {
    // this id is only used as key for the tariff
    const id = Math.random().toString(36).substring(2, 15);

    onChange([
      ...(formValues.customIssuerTariffs || []),
      {
        _id: id,
        issuerName: '',
        pricePerSession: 0,
        pricePerHour: 0,
        pricePerKwh: 0,
        costSettings: {
          ac: {
            pricePerSession: 0,
            pricePerHour: 0,
            pricePerKwh: 0,
          },
          dc: {
            pricePerSession: 0,
            pricePerHour: 0,
            pricePerKwh: 0,
          },
        },
      },
    ]);
  };

  const removeTariff = (id: string) => () => {
    onChange(formValues.customIssuerTariffs.filter((t) => t.issuerName != id));
  };

  const setField =
    (tariff: TariffProfileCustomIssuerTariff) =>
    (field: string, value: any) => {
      set(tariff, field, value);
    };

  const setActiveTariffSystem =
    (tariff: TariffProfileCustomIssuerTariff) => (e: Selected) => {
      tariff.costSettings = tariff.costSettings ?? {
        ac: {
          pricePerSession: 0,
          pricePerHour: 0,
          pricePerKwh: 0,
        },
        dc: {
          pricePerSession: 0,
          pricePerHour: 0,
          pricePerKwh: 0,
        },
      };

      if (e.ac !== 'scheduled') {
        tariff.costSettings.ac.components = [];
      }

      if (e.dc !== 'scheduled') {
        tariff.costSettings.dc.components = [];
      }

      tariff.costSettings.ac.enableEnergyMarketTracking = e.ac === 'dynamic';
      tariff.costSettings.dc.enableEnergyMarketTracking = e.dc === 'dynamic';

      if (e.ac !== 'dynamic') {
        tariff.costSettings.ac.surchargePerKwh = 0;
      }

      if (e.dc !== 'dynamic') {
        tariff.costSettings.dc.surchargePerKwh = 0;
      }
    };

  if (!user) {
    return <></>;
  }

  return (
    <>
      {formValues.customIssuerTariffs?.map((tariff) => {
        return (
          <div
            style={{
              border: 'rgba(29, 31, 33, 0.1) solid 1px',
              padding: 10,
              marginBottom: 15,
              borderRadius: '.25rem',
            }}
            key={`custom-msp-tariff-${tariff._id}`}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
              <h4
                style={{
                  margin: 'auto 0',
                }}>
                AC/DC
              </h4>
              {formValues.customIssuerTariffs.length > 1 && (
                <ActionButtonWithIcon
                  text={t('specialMspTariffs.remove', 'Remove')}
                  icon="trash-can regular"
                  onClick={removeTariff(tariff.issuerName)}
                />
              )}
            </div>
            <h4>{t('customTariffsForIssuers.issuer', 'Issuer')}</h4>
            <Form.Field>
              <Form.Input
                value={tariff.issuerName || ''}
                name="issuerName"
                label={t('customTariffsForIssuers.issuerName', 'Issuer Name')}
                required
                type="text"
                onChange={(e: any, { value }: { value: string }) => {
                  tariff.issuerName = value;
                  onChange(formValues.customIssuerTariffs);
                }}
              />
            </Form.Field>
            <AccountPlatformFeatureProvider accountId={user.accountId}>
              <TariffProfileFeatureProvider>
                <TariffCostSettings
                  sameForAcDc={sameForAcDc}
                  formValues={{
                    ...formValues,
                    costSettings: tariff.costSettings,
                  }}
                  setField={setField(tariff)}
                  setActiveTariffSystem={setActiveTariffSystem(tariff)}
                  hideHeader={true}
                />
              </TariffProfileFeatureProvider>
            </AccountPlatformFeatureProvider>
          </div>
        );
      })}
      <div style={{ paddingBottom: 20, paddingLeft: 25 }}>
        <ActionButtonWithIcon
          text={t('specialMspTariffs.add', 'Add Special Tariff')}
          icon="plus"
          onClick={addTariff}
        />
      </div>
    </>
  );
}
